/**
 * Alpine.js Trip Editor Component
 * Unified component for trip creation (modal) and inline editing (detail page)
 */

import { createDatePickerMixin } from './trip-date-picker.js';
import { initCityAutocomplete } from './city-autocomplete.js';
import { formatFutureRelative } from './time-utils.js';
import { lockBodyScroll, unlockBodyScroll } from './scroll-lock.js';
import { createTimeoutSignal } from './timeout-signal.js';

// Helper to get translation (falls back to key if i18n not available)
function t(key) {
    return window.t ? window.t(key) : key;
}

const VIEW_ACL_VALUES = ['0', '1', '2', '3'];

// Tail of the page's auto-save chain. Module scope so the edit modal's save() can wait on it.
let autoSaveInFlight = null;
const AUTO_SAVE_TIMEOUT_MS = 15000;

// Wait for queued auto-saves before a full PUT or DELETE, bounded so a hung request cannot block it
function settleAutoSaves() {
    if (!autoSaveInFlight) return Promise.resolve();
    return Promise.race([autoSaveInFlight, new Promise(resolve => setTimeout(resolve, AUTO_SAVE_TIMEOUT_MS))]);
}

// Debounce helper for auto-save. cancel() drops a pending call and reports whether one was pending.
function debounce(fn, delay) {
    let timeoutId = null;
    const debounced = function(...args) {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
            timeoutId = null;
            fn.apply(this, args);
        }, delay);
    };
    debounced.cancel = () => {
        const wasPending = timeoutId !== null;
        clearTimeout(timeoutId);
        timeoutId = null;
        return wasPending;
    };
    return debounced;
}

/**
 * Trip Editor Component Factory
 * Handles both modal and inline editing modes
 *
 * Usage in Twig:
 *   Modal: x-data="tripEditor()" with @open-trip-modal.window
 *   Inline: x-data="tripEditor({ mode: 'inline', tripData: {{ trip|json_encode }} })"
 */
export function tripEditor(config = {}) {
    return {
        // Spread date picker mixin
        ...createDatePickerMixin(),

        // Mode: 'modal' | 'inline' | 'progressive'
        // progressive = always-editable inline fields with auto-save
        mode: config.mode || 'modal',

        // Modal-specific state
        isOpen: false,

        // Inline-specific state
        isEditing: false,

        // Progressive mode state
        autoSaveStatus: '', // '', 'saving', 'saved', 'error'

        // Shared form state
        tripId: null,
        tripsUrl: null,
        city: { id: null, name: '', countryIso: '', countryName: '' },
        citySearch: '',
        startDate: '',
        endDate: '',
        description: '',
        lookingFor: [],
        tripType: null,
        viewAcl: '0',
        isSaving: false,

        // Original data for reset (inline editing)
        originalData: null,

        // Computed properties
        get hasDates() {
            return this.startDate && this.endDate;
        },

        get dateDisplay() {
            void this.$store?.i18n?.ready;
            if (!this.startDate) return t('common.post_trip_when_button');
            if (!this.endDate) return this.formatDateShort(this.startDate) + ' - ?';
            return this.formatDateShort(this.startDate) + ' - ' + this.formatDateShort(this.endDate);
        },

        get canSave() {
            return this.city.id && this.startDate && this.endDate;
        },

        get viewAclLabel() {
            void this.$store?.i18n?.ready;
            const labels = {
                '0': t('common.post_visibility_all_users'),
                '1': t('common.post_visibility_members'),
                '2': t('common.post_visibility_my_friends'),
                '3': t('common.post_visibility_only_me')
            };
            return labels[this.viewAcl] || t('common.post_visibility_all_users');
        },

        /**
         * Get relative time label for the trip start date
         */
        get relativeTimeLabel() {
            if (!this.startDate) return '';
            return formatFutureRelative(this.startDate);
        },

        get isPastTrip() {
            if (!this.endDate) return false;
            const end = new Date(this.endDate + 'T23:59:59');
            return end < new Date();
        },

        /**
         * Format a date range in the site language, e.g. "January 21 – 23, 2026".
         * formatRange keeps both years when the range crosses a year boundary.
         */
        formatDateRange(startDate, endDate) {
            if (!startDate || !endDate) return '';
            const start = new Date(startDate + 'T00:00:00');
            const end = new Date(endDate + 'T00:00:00');
            try {
                const fmt = new Intl.DateTimeFormat(document.documentElement.lang || undefined, {
                    month: 'long', day: 'numeric', year: 'numeric',
                    calendar: 'gregory', numberingSystem: 'latn'
                });
                if (typeof fmt.formatRange === 'function') {
                    return fmt.formatRange(start, end);
                }
                return `${fmt.format(start)} – ${fmt.format(end)}`;
            } catch {
                return `${startDate} – ${endDate}`;
            }
        },

        /**
         * Initialize component (called automatically by Alpine)
         */
        init() {
            // Load initial trip data if provided in config
            if (config.tripData) {
                this.loadTripData(config.tripData);
                this.originalData = { ...config.tripData };
            }
        },

        /**
         * Load trip data into form fields
         * @param {Object} tripData
         */
        loadTripData(tripData) {
            this.tripId = tripData.id || null;
            this.tripsUrl = tripData.trips_url || null;
            this.city = {
                id: tripData.city_id || null,
                name: tripData.city_name || '',
                countryIso: (tripData.country_code || '').toLowerCase(),
                countryName: tripData.country_name || ''
            };
            this.citySearch = tripData.city_name || '';
            this.startDate = tripData.start_date || '';
            this.endDate = tripData.end_date || '';
            this.description = tripData.description || '';
            this.lookingFor = tripData.looking_for || [];
            this.tripType = tripData.trip_type || null;
            this.viewAcl = String(tripData.view_acl || 0);
        },

        // === Modal Methods ===

        /**
         * Open modal (for modal mode)
         * @param {Object|null} tripData - Trip data for editing, null for new trip
         */
        open(tripData = null) {
            if (this.isOpen) return;
            if (tripData && tripData.id) {
                this.loadTripData(tripData);
                const ref = this.startDate ? new Date(this.startDate + 'T00:00:00') : new Date();
                this.datePickerMonth = ref.getMonth();
                this.datePickerYear = ref.getFullYear();
                this.datePickerStep = 'start';
            } else {
                this.reset();
            }
            this.showDatePicker = false;
            this.isOpen = true;
            lockBodyScroll();
            this.$nextTick(() => {
                this.initCityAutocomplete();
                if (!this.tripId) {
                    this.$refs.cityInput?.focus();
                }
                window.LucideIcons?.initialize(this.$el);
            });
        },

        /**
         * Close modal (for modal mode)
         */
        close() {
            if (!this.isOpen) return;
            this.isOpen = false;
            this.showDatePicker = false;
            unlockBodyScroll();
        },

        /**
         * Open modal and immediately trigger delete confirmation (for modal mode)
         * Used by trip-card.twig delete button
         * @param {Object} tripData - Trip data to delete
         */
        openAndDelete(tripData) {
            if (tripData && tripData.id) {
                this.loadTripData(tripData);
                this.confirmDelete();
            }
        },

        // === Inline Methods ===

        /**
         * Start inline editing (for inline mode)
         */
        startEdit() {
            this.isEditing = true;
            this.$nextTick(() => {
                this.initCityAutocomplete();
            });
        },

        /**
         * Cancel inline editing and restore original values (for inline mode)
         */
        cancelEdit() {
            if (this.originalData) {
                this.loadTripData(this.originalData);
            }
            this.isEditing = false;
            this.showDatePicker = false;
        },

        // === Shared Methods ===

        /**
         * Reset form to initial state (for creating new trip)
         */
        reset() {
            this.tripId = null;
            this.tripsUrl = null;
            this.city = { id: null, name: '', countryIso: '', countryName: '' };
            this.citySearch = '';
            this.startDate = '';
            this.endDate = '';
            this.description = '';
            this.lookingFor = [];
            this.tripType = null;
            this.viewAcl = '0';
            this.resetDatePicker();
        },

        /**
         * Initialize city autocomplete on the input element
         */
        initCityAutocomplete() {
            const input = this.$refs.cityInput;
            if (!input) return;
            initCityAutocomplete(input, (cityData) => {
                this.city = {
                    id: cityData.id,
                    name: cityData.name,
                    countryIso: cityData.countryIso,
                    countryName: ''
                };
                this.citySearch = cityData.name;
            });

            // The modal reuses one input across open() calls, so bind its listeners once
            if (input.dataset.tripCityBound) return;
            input.dataset.tripCityBound = '1';

            // Select all text on focus so user can immediately type to replace
            input.addEventListener('focus', () => {
                // Small delay to ensure the focus has completed
                setTimeout(() => input.select(), 0);
            });

            // Keep citySearch in sync with the actual input value as the user types
            // This prevents stale values when Alpine re-renders
            const syncCity = () => {
                this.citySearch = input.value;
                // Clear city.id when user manually edits the input (they must re-select from autocomplete)
                if (this.city.id && input.value !== this.city.name) {
                    this.city = { id: null, name: '', countryIso: '', countryName: '' };
                }
            };
            input.addEventListener('input', syncCity);
            // Arrow-key list navigation sets the value without an input event
            input.addEventListener('blur', syncCity);
        },

        /**
         * Toggle a "looking for" tag
         * @param {string} tag - Tag name (travel-buddy, local-tips, meetup)
         */
        toggleLookingFor(tag) {
            const idx = this.lookingFor.indexOf(tag);
            if (idx === -1) {
                this.lookingFor.push(tag);
            } else {
                this.lookingFor.splice(idx, 1);
            }
            // Auto-save in progressive mode
            if (this.mode === 'progressive') {
                this.autoSave();
            }
        },

        /**
         * Select a trip type (single select)
         * @param {string|null} type - Trip type (vacation, business, etc.) or null to clear
         */
        selectTripType(type) {
            this.tripType = type;
            // Auto-save in progressive mode
            if (this.mode === 'progressive') {
                this.autoSave();
            }
        },

        /**
         * Cycle through view ACL options
         */
        cycleViewAcl() {
            const currentIndex = VIEW_ACL_VALUES.indexOf(this.viewAcl);
            this.viewAcl = VIEW_ACL_VALUES[(currentIndex + 1) % VIEW_ACL_VALUES.length];
            // Auto-save in progressive mode
            if (this.mode === 'progressive') {
                this.autoSave();
            }
        },

        // === Progressive Mode Methods ===

        /**
         * Auto-save with debounce (for progressive mode)
         * Called when description, looking-for, trip type, or visibility changes
         */
        autoSave: debounce(function() {
            this.runAutoSave();
        }, 800),

        /**
         * Run one auto-save after any earlier one, so PUTs from this page land in order
         * @returns {Promise<void>}
         */
        runAutoSave() {
            const run = (autoSaveInFlight || Promise.resolve()).then(() => this.performAutoSave());
            autoSaveInFlight = run;
            run.finally(() => {
                if (autoSaveInFlight === run) autoSaveInFlight = null;
            });
            return run;
        },

        /**
         * Open the edit modal from the saved destination and the live detail fields.
         * A pending auto-save is sent now so a cancelled modal does not lose it.
         */
        openEditModal() {
            if (this.autoSave.cancel()) this.runAutoSave();
            window.openTripModal(this.toTripData());
        },

        async performAutoSave() {
            if (!this.tripId || !this.canSave) return;

            this.autoSaveStatus = 'saving';
            const timeout = createTimeoutSignal(AUTO_SAVE_TIMEOUT_MS);
            try {
                const token = await window.getApiToken();
                const data = {
                    city_id: this.city.id,
                    start_date: this.startDate,
                    end_date: this.endDate,
                    description: this.description,
                    view_acl: this.viewAcl,
                    looking_for: JSON.stringify(this.lookingFor),
                    trip_type: this.tripType || ''
                };

                const response = await fetch(`/v1/trip/${this.tripId}`, {
                    method: 'PUT',
                    headers: {
                        'X-AUTH-TOKEN': token,
                        'Accept': 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                        'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8'
                    },
                    body: new URLSearchParams(data),
                    credentials: 'include',
                    signal: timeout.signal
                });

                if (response.ok) {
                    this.autoSaveStatus = 'saved';
                    // Update original data
                    this.originalData = {
                        ...this.originalData,
                        description: this.description,
                        looking_for: [...this.lookingFor],
                        trip_type: this.tripType,
                        view_acl: this.viewAcl
                    };
                    // Clear status after 2 seconds
                    setTimeout(() => {
                        if (this.autoSaveStatus === 'saved') {
                            this.autoSaveStatus = '';
                        }
                    }, 2000);
                } else {
                    this.autoSaveStatus = 'error';
                }
            } catch (e) {
                console.error('Auto-save error:', e);
                this.autoSaveStatus = 'error';
            } finally {
                timeout.clear();
            }
        },

        /**
         * Handle description blur - trigger auto-save in progressive mode
         */
        onDescriptionBlur() {
            if (this.mode === 'progressive' && this.description !== this.originalData?.description) {
                this.autoSave();
            }
        },

        /**
         * Save trip (create or update)
         */
        async save() {
            if (!this.canSave || this.isSaving) return;

            this.isSaving = true;
            try {
                await settleAutoSaves();
                const token = await window.getApiToken();
                const url = this.tripId ? `/v1/trip/${this.tripId}` : '/v1/trip';
                const method = this.tripId ? 'PUT' : 'POST';

                const data = {
                    city_id: this.city.id,
                    start_date: this.startDate,
                    end_date: this.endDate,
                    description: this.description,
                    view_acl: this.viewAcl,
                    looking_for: JSON.stringify(this.lookingFor),
                    trip_type: this.tripType || ''
                };

                const response = await fetch(url, {
                    method: method,
                    headers: {
                        'X-AUTH-TOKEN': token,
                        'Accept': 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                        'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8'
                    },
                    body: new URLSearchParams(data),
                    credentials: 'include'
                });

                if (response.ok) {
                    const tripData = await response.json();

                    if (this.mode === 'modal') {
                        this.close();
                        // Emit event for trips list to update
                        window.dispatchEvent(new CustomEvent('trip-saved', { detail: tripData }));
                        this.reloadToTrips();
                    } else {
                        // Inline mode: keep current form values (they are already correct)
                        // and update originalData so cancel works correctly
                        this.originalData = {
                            id: this.tripId,
                            city_id: this.city.id,
                            city_name: this.city.name,
                            country_code: this.city.countryIso?.toUpperCase(),
                            country_name: this.city.countryName,
                            start_date: this.startDate,
                            end_date: this.endDate,
                            description: this.description,
                            looking_for: [...this.lookingFor],
                            trip_type: this.tripType,
                            view_acl: this.viewAcl
                        };
                        this.isEditing = false;
                        // Emit event for any listeners
                        window.dispatchEvent(new CustomEvent('trip-saved', { detail: tripData }));
                    }
                } else {
                    const data = await response.json();
                    this.showError(data.error?.message || t('common.trip_save_error'));
                }
            } catch (e) {
                console.error('Save error:', e);
                this.showError(t('common.trip_save_error'));
            }
            this.isSaving = false;
        },

        /**
         * Show delete confirmation dialog
         */
        confirmDelete() {
            if (!this.tripId) return;

            if (window.ConfirmModal) {
                window.ConfirmModal.show({
                    title: t('common.trip_delete_title'),
                    message: t('common.trip_delete_message'),
                    confirmText: t('common.trip_delete_button'),
                    type: 'danger',
                    onConfirm: () => this.doDelete()
                });
            } else {
                // Fallback to native confirm
                if (confirm(t('common.trip_delete_confirm'))) {
                    this.doDelete();
                }
            }
        },

        /**
         * Execute trip deletion
         */
        async doDelete() {
            this.isSaving = true;
            try {
                await settleAutoSaves();
                const token = await window.getApiToken();
                const response = await fetch(`/v1/trip/${this.tripId}`, {
                    method: 'DELETE',
                    headers: {
                        'X-AUTH-TOKEN': token,
                        'Accept': 'application/json',
                        'X-Requested-With': 'XMLHttpRequest'
                    },
                    credentials: 'include'
                });

                if (response.ok) {
                    this.close();
                    // A page listing the trip as a card reloads so its counts and carousel stay consistent.
                    // The detail page has no card and leaves the deleted trip for the owner's trips list.
                    const hasCard = this.mode === 'modal' && document.querySelector(`[data-trip-id="${this.tripId}"]`);
                    if (!hasCard && this.tripsUrl) {
                        window.location.href = this.tripsUrl;
                    } else {
                        this.reloadToTrips();
                    }
                } else {
                    this.showError(t('common.trip_delete_error'));
                }
            } catch (e) {
                console.error('Delete error:', e);
                this.showError(t('common.trip_delete_error'));
            }
            this.isSaving = false;
        },

        /**
         * Reload the page, scrolled to its trips section when it has one
         */
        reloadToTrips() {
            if (document.getElementById('trips')) {
                window.location.hash = '#trips';
            }
            window.location.reload();
        },

        /**
         * Snapshot of the current form state in the server trip shape, for handing to the edit modal
         * @returns {Object}
         */
        toTripData() {
            return {
                id: this.tripId,
                trips_url: this.tripsUrl,
                city_id: this.city.id,
                city_name: this.city.name,
                country_code: this.city.countryIso?.toUpperCase(),
                country_name: this.city.countryName,
                start_date: this.startDate,
                end_date: this.endDate,
                description: this.description,
                looking_for: [...this.lookingFor],
                trip_type: this.tripType,
                view_acl: this.viewAcl
            };
        },

        /**
         * Show error message to user
         * @param {string} message
         */
        showError(message) {
            if (window.ConfirmModal) {
                window.ConfirmModal.show({
                    title: t('common.error_title'),
                    message: message,
                    confirmText: t('common.ok'),
                    type: 'info',
                    showCancel: false
                });
            } else {
                alert(message);
            }
        }
    };
}

// Register with Alpine when available
if (window.Alpine) {
    window.Alpine.data('tripEditor', tripEditor);
}

// Global function for opening trip modal (backwards compatibility with trip-card.twig)
window.openTripModal = function(tripData) {
    window.dispatchEvent(new CustomEvent('open-trip-modal', { detail: tripData }));
};

// Global function for deleting a trip via the modal
window.deleteTripModal = function(tripData) {
    window.dispatchEvent(new CustomEvent('delete-trip-modal', { detail: tripData }));
};

// Export for testing and direct usage
export default tripEditor;
